import { useQuery } from "@tanstack/react-query";
import { clsx } from "clsx";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { useMemo, useState } from "react";

import { listPosts } from "@/adapters/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { formatPostDate } from "@/features/feed/utils/format";
import { useActiveContext } from "@/store/use-active-context";

const MAX_CANDIDATES = 50;

export interface ContinuationTarget {
	id: string;
	title: string;
	date: string;
}

interface ContinuationPickerProps {
	value: ContinuationTarget | undefined;
	onChange: (next: ContinuationTarget | undefined) => void;
}

function normalize(s: string) {
	return s
		.toLowerCase()
		.normalize("NFD")
		.replace(/\p{Diacritic}/gu, "");
}

export function ContinuationPicker({
	value,
	onChange,
}: ContinuationPickerProps) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const activeObraId = useActiveContext((state) => state.activeObraId);

	const candidatesQuery = useQuery({
		queryKey: ["posts", activeObraId, "continuation-candidates"],
		enabled: !!activeObraId && open,
		queryFn: async (): Promise<ContinuationTarget[]> => {
			const { items } = await listPosts({
				constructionId: activeObraId as string,
				pageSize: MAX_CANDIDATES,
			});
			return items
				.filter((item) => item.status === "published")
				.map((item) => ({
					id: item.id,
					title: item.title,
					date: item.occurred_at,
				}));
		},
		staleTime: 30_000,
	});

	const candidates = candidatesQuery.data ?? [];

	const filtered = useMemo(() => {
		const q = normalize(query.trim());
		if (!q) return candidates;
		return candidates.filter((post) => normalize(post.title).includes(q));
	}, [candidates, query]);

	function pick(next: ContinuationTarget | undefined) {
		onChange(next);
		setOpen(false);
		setQuery("");
	}

	return (
		<Popover open={open} onOpenChange={setOpen} modal>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="outline"
					role="combobox"
					aria-expanded={open}
					aria-label={value ? value.title : "Nenhum — relato independente"}
					className="post-picker__trigger"
				>
					<span
						className={clsx("post-picker__trigger-text", !value && "is-empty")}
					>
						{value ? value.title : "Nenhum — relato independente"}
					</span>
					<ChevronsUpDown className="post-picker__chevron" />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="post-picker__content" align="start">
				<div className="post-picker__search">
					<div className="post-picker__search-field">
						<Search className="post-picker__search-icon" />
						<Input
							autoFocus
							value={query}
							onChange={(event) => setQuery(event.target.value)}
							placeholder="Buscar relato..."
							className="post-picker__search-input"
						/>
					</div>
				</div>
				<div className="post-picker__list">
					<button
						type="button"
						onClick={() => pick(undefined)}
						className={clsx("post-picker__option", !value && "is-active")}
					>
						<span className="post-picker__none-label">
							Nenhum — relato independente
						</span>
						{!value && <Check />}
					</button>
					{candidatesQuery.isPending && open ? (
						<p className="post-picker__empty">Carregando relatos…</p>
					) : filtered.length === 0 ? (
						<p className="post-picker__empty">Nenhum relato encontrado.</p>
					) : (
						filtered.map((post) => {
							const active = post.id === value?.id;
							return (
								<button
									key={post.id}
									type="button"
									onClick={() => pick(post)}
									className={clsx("post-picker__item", active && "is-active")}
								>
									<span className="post-picker__item-title">{post.title}</span>
									<span className="post-picker__item-date">
										{formatPostDate(post.date)}
									</span>
									{active && <Check />}
								</button>
							);
						})
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
